import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { GRID, initialSnake, snakeStep, shuffledCards, memoryScore } from './activeBreakGames';
import './PausaActiva.css';
import FreeKickGame from './FreeKickGame';

const API = window.__CRM_API__ || `${window.location.origin}/api`;
const GAMES = [
  { id: 'snake', icon: '🐍', title: 'Culebrita', description: 'Recoge frutas y evita chocar. Cada fruta vale 10 puntos.' },
  { id: 'memory', icon: '🧠', title: 'Memoria', description: 'Encuentra las seis parejas. Menos intentos, más puntos.' },
  { id: 'football_story', icon: '⚽', title: 'Jugadas de gol', description: 'Construye cinco jugadas: pasa, evita las intercepciones y define frente al arquero. Tres intentos por jugada.' },
];
function useTick(callback, delay, enabled) {
  const latest = useRef(callback);
  latest.current = callback;
  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setInterval(() => latest.current(), delay);
    return () => clearInterval(timer);
  }, [delay, enabled]);
}
function Snake({ running, onFinish }) {
  const [state, setState] = useState(initialSnake);
  const direction = useRef('right'), ended = useRef(false);
  useEffect(() => {
    if (!running || state.done) return undefined;
    const handleKey = event => {
      // Capture game keys even while Comenzar or a touch control has focus,
      // but never hijack typing/search fields or browser shortcuts.
      if (event.ctrlKey || event.metaKey || event.altKey ||
          event.target?.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"]')) return;
      const key = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right', w: 'up', s: 'down', a: 'left', d: 'right' }[event.key.length === 1 ? event.key.toLowerCase() : event.key];
      if (!key) return;
      event.preventDefault();
      direction.current = key;
    };
    window.addEventListener('keydown', handleKey, { capture: true, passive: false });
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [running, state.done]);
  useTick(() => setState(current => snakeStep(current, direction.current)), 150, running && !state.done);
  useEffect(() => {
    if (state.done && !ended.current) { ended.current = true; onFinish(state.score); }
  }, [state.done, state.score, onFinish]);
  const turn = value => { direction.current = value; };
  return <><p className="pa-game-score">Puntos: <strong>{state.score}</strong></p>
    <div className="pa-snake" tabIndex={0} role="group" aria-label="Tablero de culebrita. Usa las flechas o los controles de dirección.">
      {Array.from({ length: GRID * GRID }, (_, n) => {
        const x = n % GRID, y = Math.floor(n / GRID);
        const body = state.snake.findIndex(p => p[0] === x && p[1] === y);
        const food = state.food?.[0] === x && state.food?.[1] === y;
        return <span key={n} className={body === 0 ? 'head' : body > 0 ? 'body' : food ? 'food' : ''} />;
      })}
    </div><div className="pa-directions" aria-label="Controles táctiles">
      {[['up', '↑'], ['left', '←'], ['down', '↓'], ['right', '→']].map(([id, label]) => <button key={id} aria-label={{ up: 'Arriba', down: 'Abajo', left: 'Izquierda', right: 'Derecha' }[id]} onClick={() => turn(id)} disabled={!running}>{label}</button>)}
    </div><small>En computador: al comenzar, usa las flechas o WASD sin hacer clic en el tablero. En móvil: usa los botones.</small></>;
}
const SYMBOLS = ['🌿', '☀️', '🌊', '🍋', '🌸', '⭐'];
function Memory({ running, onFinish }) {
  const [cards] = useState(shuffledCards), [open, setOpen] = useState([]), [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const finished = useRef(false);
  useEffect(() => {
    if (open.length !== 2 || !running) return undefined;
    const timer = setTimeout(() => {
      if (cards[open[0]] === cards[open[1]]) setMatched(current => [...current, ...open]);
      setOpen([]);
    }, 650);
    return () => clearTimeout(timer);
  }, [open, cards, running]);
  useEffect(() => {
    if (matched.length === 12 && !finished.current) { finished.current = true; onFinish(memoryScore(moves)); }
  }, [matched, moves, onFinish]);
  const flip = index => {
    if (!running || open.length === 2 || open.includes(index) || matched.includes(index)) return;
    if (open.length === 1) setMoves(value => value + 1);
    setOpen(current => [...current, index]);
  };
  return <><p className="pa-game-score">Intentos: <strong>{moves}</strong> · Parejas: {matched.length / 2}/6</p>
    <div className="pa-memory">{cards.map((card, index) => {
      const visible = open.includes(index) || matched.includes(index);
      return <button key={index} onClick={() => flip(index)} disabled={!running || matched.includes(index)}
        className={visible ? 'revealed' : ''} aria-label={visible ? `Carta ${index + 1}: ${SYMBOLS[card]}` : `Voltear carta ${index + 1}`}>{visible ? SYMBOLS[card] : '?'}</button>;
    })}</div><small>1200 puntos con seis intentos; cada intento adicional resta 40 (mínimo 100).</small></>;
}
export default function PausaActiva() {
  const playPanel = useRef(null);
  const [rankingLoading, setRankingLoading] = useState(true);
  const [game, setGame] = useState('snake'), [round, setRound] = useState(0), [status, setStatus] = useState('ready');
  const [result, setResult] = useState(null), [ranking, setRanking] = useState(null), [error, setError] = useState('');
  const [saving, setSaving] = useState(false), [pending, setPending] = useState(null);
  const selected = GAMES.find(item => item.id === game);
  const refresh = useCallback(async (signal) => {
    setRankingLoading(true);
    try { const response = await axios.get(`${API}/active-break/scores`, { params: { game }, signal }); setRanking(response.data); }
    catch (err) { if (!axios.isCancel(err)) setError('No se pudo cargar la clasificación. Puedes actualizarla nuevamente.'); }
    finally { if (!signal?.aborted) setRankingLoading(false); }
  }, [game]);
  useEffect(() => { const controller = new AbortController(); setRanking(null); refresh(controller.signal); return () => controller.abort(); }, [refresh]);
  useEffect(() => {
    const pause = () => { if (document.hidden) setStatus(value => value === 'playing' ? 'paused' : value); };
    const blur = () => setStatus(value => value === 'playing' ? 'paused' : value);
    document.addEventListener('visibilitychange', pause); window.addEventListener('blur', blur);
    return () => { document.removeEventListener('visibilitychange', pause); window.removeEventListener('blur', blur); };
  }, []);
  const save = async payload => {
    setSaving(true); setError('');
    try { await axios.post(`${API}/active-break/scores`, payload); setPending(null); await refresh(); }
    catch { setPending(payload); setError('No se guardó el puntaje. Conservamos el resultado aquí para que puedas reintentar.'); }
    finally { setSaving(false); }
  };
  const finish = score => { setStatus('done'); setResult(score); save({ game, score }); };
  const start = () => {
    setRound(value => value + 1); setResult(null); setStatus('playing');
    playPanel.current?.scrollIntoView?.({ block: 'start', behavior: 'instant' });
  };
  const choose = id => {
    if (id === game) return;
    if ((status === 'playing' || status === 'paused' || pending) && !window.confirm('¿Cambiar de juego? La partida sin terminar o sin guardar se perderá.')) return;
    setGame(id); setRound(value => value + 1); setStatus('ready'); setResult(null); setPending(null); setError('');
  };
  return <section className="pa-workspace">
    <header className="pa-heading"><div><span>UN RESPIRO PARA EL EQUIPO</span><h1>☕ Pausa Activa</h1><p>Desconecta unos minutos, estira las manos y vuelve con energía.</p></div><span className="pa-badge">3 juegos · récords compartidos</span></header>
    <div className="pa-game-tabs" aria-label="Seleccionar juego">{GAMES.map(item => <button disabled={saving} key={item.id} aria-pressed={game === item.id} onClick={() => choose(item.id)}><span>{item.icon}</span><strong>{item.title}</strong></button>)}</div>
    <div className="pa-layout"><section className="pa-play" ref={playPanel}><h2>{selected.icon} {selected.title}</h2><p>{selected.description}</p>
      <div className="pa-toolbar">
        {(status === 'ready' || status === 'done') && <button disabled={saving || !!pending} onClick={start}>{status === 'ready' ? 'Comenzar' : 'Jugar otra vez'}</button>}
        {(status === 'playing' || status === 'paused') && <button onClick={() => setStatus(status === 'playing' ? 'paused' : 'playing')}>{status === 'paused' ? 'Continuar' : 'Pausar'}</button>}
        <span role="status">{status === 'ready' ? 'Listo para comenzar' : status === 'paused' ? 'En pausa; tu partida se conserva' : status === 'done' ? `Partida terminada: ${result} puntos` : '¡Vamos!'}</span>
      </div>
      <div className="pa-board" key={`${game}-${round}`}>
        {game === 'snake' ? <Snake running={status === 'playing'} onFinish={finish} /> : game === 'memory' ? <Memory running={status === 'playing'} onFinish={finish} /> : <FreeKickGame running={status === 'playing'} onFinish={finish} />}
      </div>
      {saving && <p role="status">Guardando puntaje…</p>}
      {error && <p role="alert" className="pa-error">{error} {pending && <button disabled={saving} onClick={() => save(pending)}>Reintentar guardado</button>}</p>}
    </section>
    <aside className="pa-ranking"><h2>🏆 Clasificación del equipo</h2><p>{selected.title} · Mejores puntajes</p><div className="pa-best">Tu récord <strong>{ranking?.personal_best ?? '—'}</strong></div>
      <button className="pa-refresh" onClick={() => { setError(''); refresh(); }}>Actualizar clasificación</button>
      {!ranking ? <p>{rankingLoading ? 'Cargando clasificación…' : 'Clasificación no disponible. Pulsa actualizar para reintentar.'}</p> : !ranking.leaders.length ? <p>Aún no hay récords. ¡Sé la primera persona en jugar!</p> : <ol>{ranking.leaders.map((row, index) => <li key={index} className={row.mine ? 'mine' : ''}><span><b>{row.name}{row.mine ? ' (tú)' : ''}</b><small>{new Date(row.achieved_at).toLocaleDateString()}</small></span><strong>{row.score}</strong></li>)}</ol>}
      <small>Se conserva tu mejor resultado por juego. Los usuarios activos pueden ver hasta 100 récords. Competencia recreativa, no una evaluación laboral.</small>
    </aside></div>
  </section>;
}
